export type DeviceState = 'disconnected' | 'connected' | 'logged_in'

export interface DeviceInfo {
  id: string
  phone_number: string
  display_name: string
  state: DeviceState
  jid: string
  created_at: string
}

export type WsClientCode = 'FETCH_DEVICES'
export type WsServerCode =
  // Снимок статусов провайдеров при подключении и смене прав (DEV-1669)
  | 'PROVIDER_STATUS'
  | 'LIST_DEVICES'
  | 'LOGIN_SUCCESS'
  | 'DEVICE_CONNECTED'
  | 'DEVICE_REMOVED'
  | 'NEW_MESSAGE'
  | 'MESSAGE_STATUS'
  | 'MESSAGE_REACTION'
  | 'MESSAGE_MEDIA_UPDATE'
  | 'CLIENT_FILE_ADDED'
  | 'WHATSAPP_CALL_STATE'
  // Zalo events
  | 'NEW_ZALO_MESSAGE'
  | 'ZALO_MESSAGE_SENT'
  | 'ZALO_FOLLOWER_ADDED'
  | 'ZALO_FOLLOWER_REMOVED'
  // Telegram events (DEV-878)
  | 'TELEGRAM_QR_UPDATE'
  | 'TELEGRAM_LOGIN_STATUS'
  | 'TELEGRAM_ACCOUNT_STATUS'
  | 'NEW_TELEGRAM_MESSAGE'
  | 'TELEGRAM_MESSAGE_SENT'
  | 'TELEGRAM_MESSAGE_STATUS'
  | 'TELEGRAM_MESSAGE_MEDIA_UPDATE'
  | 'TELEGRAM_MESSAGE_EDITED'
  | 'TELEGRAM_MESSAGE_DELETED'
  | 'TELEGRAM_MESSAGE_REACTION'
  | 'TELEGRAM_TYPING'
  // Telegram call events (DEV-924)
  | 'TELEGRAM_CALL_SDP_OFFER'
  | 'TELEGRAM_CALL_ICE'
  | 'TELEGRAM_CALL_STATE'
  | 'TELEGRAM_CALL_ENDED'
  | 'TELEGRAM_CALL_INCOMING'
  | 'TELEGRAM_CALL_ANALYSIS_COMPLETED'
  // Ответ на TELEGRAM_CALL_SYNC: входящие для вкладки, открытой во время звонка (DEV-2684)
  | 'TELEGRAM_CALL_RINGING'
  // Звонки на общий номер WhatsApp Business (DEV-2418)
  | 'WABA_CALL_INCOMING'
  | 'WABA_CALL_SDP_OFFER'
  | 'WABA_CALL_ICE'
  | 'WABA_CALL_STATE'
  | 'WABA_CALL_ENDED'
  | 'WABA_CALL_ANALYSIS_COMPLETED'
  // Ответ на WABA_CALL_SYNC: звонящие для вкладки, открытой во время звонка (DEV-2570)
  | 'WABA_CALL_RINGING'

export interface WsClientMessage {
  code: WsClientCode
  message: string
  result: null
}

/**
 * Почему не ушло сообщение WhatsApp (DEV-2418): по коду вкладка подбирает текст
 * тоста. payment_required — у WABA общего номера проблема со способом оплаты
 * в Meta (DEV-2482).
 */
export interface WhatsAppSendFailure {
  code: 'payment_required' | 'generic'
  /** Метка времени — два одинаковых провала подряд должны дать два тоста. */
  at: number
}

export interface WsServerMessage {
  code: WsServerCode
  message: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result: any
}

/** WS payload — snake_case (built manually on backend) */
export interface WsNewMessagePayload {
  id: string
  chat_jid: string
  sender_jid: string
  content: string
  timestamp: string
  is_from_me: boolean
  media_type: MediaType
  push_name: string
  // DEV-1667: кто из сотрудников отправил из CRM.
  author_type?: string
  author_manager_id?: string
}

/** WS payload — snake_case */
export interface WsMessageStatusPayload {
  id: string
  chat_jid: string
  status: MessageStatus
}

/** WS payload — snake_case */
export interface WsMessageReactionPayload {
  chat_jid: string
  sender_jid: string
  target_message_id: string
  emoji: string
  timestamp: string
  is_from_me: boolean
}

/** API response — camelCase (Go JSON serialization) */
export interface ChatInfo {
  jid: string
  name: string
  lastMessageTime: string
  unreadCount?: number
  isPinned?: boolean
  isArchived?: boolean
  ephemeralExpiration?: number
  // DEV-1686 (только WhatsApp Business): когда истекает 24-часовое окно свободной
  // переписки (RFC3339). Пусто — клиент ещё ни разу не писал, окно закрыто.
  waWindowExpiresAt?: string
  // DEV-1670 (только WhatsApp Business): карточка клиента видна, а писать с общего
  // номера роли нельзя (нет communications:create) — переписка только для чтения.
  readOnly?: boolean
  createdAt: string
  updatedAt: string
}

// DEV-302: vinprop.common.v1.CursorPageInfo (total — int64 → строка в protojson)
export interface Pagination {
  nextPageToken?: string
  total?: number | string
}

export interface ChatsResponse {
  data: ChatInfo[]
  pagination: Pagination
}

export type MediaType = 'text' | 'image' | 'document' | 'audio' | 'video' | 'sticker' | 'call'

export type CallStatus = 'ended' | 'missed' | 'rejected' | 'no_answer'
export type CallDirection = 'incoming' | 'outgoing'

/** Payload encoded as JSON in MessageInfo.content when mediaType === 'call'. */
export interface CallMessagePayload {
  direction: CallDirection
  status: CallStatus
  duration_sec: number
  started_at: string
  answered_at?: string
  ended_at?: string
  reason?: string
  // DEV-2443: наша запись звонка — пузырь раскрывает её в панели звонков.
  call_id?: string
}
export type MessageStatus = 'pending' | 'sent' | 'delivered' | 'read' | 'failed'

export interface MessageReaction {
  emoji: string
  senderJid: string
  isFromMe: boolean
  timestamp: string
}

/** API response — camelCase (Go JSON serialization with omitempty) */
export interface MessageInfo {
  id: string
  chatJid: string
  senderJid: string
  content: string
  timestamp: string
  isFromMe?: boolean
  mediaType?: MediaType
  filename?: string
  url?: string
  fileLength?: number
  status: MessageStatus
  isReadByManager?: boolean
  reactions?: MessageReaction[]
  createdAt: string
  updatedAt: string
  // DEV-1835 (только Telegram): аккаунт-источник ('me' | 'support' | uuid) и ключ,
  // уникальный в объединённой ленте: `${source}|${chatJid}|${id}`. У WA/Zalo пусто.
  sourceManagerId?: string
  uid?: string
  // DEV-1667: кто из сотрудников отправил сообщение из CRM. Пусто — входящее или
  // отправлено с телефона мимо CRM. 'bot' — AI-ассистент (DEV-1676), не сотрудник.
  authorType?: string
  authorManagerId?: string
}

/**
 * DEV-1676: authorType исходящего, которое написал AI-ассистент (ai-chatbot) с общего
 * номера WhatsApp, аккаунта поддержки Telegram или из сессии личного Telegram
 * менеджера. Не моё и не коллеги: подпись
 * «AI-ассистент» с бейджем, и эхо бота не подтверждает мои неотправленные пузыри.
 */
export const BOT_AUTHOR_TYPE = 'bot'

/**
 * DEV-1835: подпись источника на пузыре объединённой ленты Telegram, по
 * sourceManagerId. incoming — «→ в Поддержку», outgoing — «Поддержка» /
 * «<Имя> · менеджер · личный TG». readonly — реакции на сообщения источника
 * недоступны (чужой менеджерский чат).
 */
export interface MessageSourceLabel {
  incoming: string
  outgoing: string
  tone: 'support' | 'manager'
  readonly?: boolean
}

export interface MessagesResponse {
  data: MessageInfo[]
  chatInfo: ChatInfo
  pagination: Pagination
}

export interface SendTextRequest {
  phone: string
  message: string
  reply_message_id?: string
  client_id?: string
}

/**
 * DEV-1686: шаблон WhatsApp Business. status — APPROVED | PENDING | REJECTED
 * (вне окна 24ч отправить можно только APPROVED), body содержит плейсхолдеры
 * `{{1}}`, `{{2}}` по числу параметров.
 */
export interface WhatsAppTemplate {
  name: string
  language: string
  status: string
  category?: string
  body?: string
}

export interface WhatsAppTemplatesResponse {
  data: WhatsAppTemplate[]
}

/**
 * DEV-2566: состояние общего номера для админки. Пустые и false-поля шлюз может
 * не прислать вовсе, поэтому все они необязательные.
 */
export interface WabaStatusResponse {
  enabled?: boolean
  phoneNumberId?: string
  /** Номер для показа: после перезапуска сервиса пуст до первого события. */
  displayPhoneNumber?: string
  callsEnabled?: boolean
  callsRecording?: boolean
  /** AVAILABLE, LIMITED или BLOCKED; пусто, если Meta не ответила. */
  canSendMessage?: string
  /** Пояснения Meta к статусу, по-английски. */
  notes?: string[]
}

export interface SendWhatsAppTemplateRequest {
  phone: string
  name: string
  language: string
  parameters?: string[]
  client_id?: string
}

export interface SendImageRequest {
  phone: string
  image: string
  caption?: string
  view_once?: boolean
  client_id?: string
}

export interface SendDocumentRequest {
  phone: string
  document: string
  file_name?: string
  client_id?: string
}

/** WS payload — snake_case */
export interface WsMessageMediaUpdatePayload {
  id: string
  chat_jid: string
  media_type: MediaType
  url?: string
  filename?: string
  file_length?: number
}
